import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings,
  Download,
  Upload,
  Database,
  CheckCircle,
  AlertTriangle,
  Loader2,
  FileJson,
  Calendar,
  Wrench,
  Webhook,
  Copy,
  ExternalLink,
  Archive,
  RotateCcw,
  ShoppingBag,
  RefreshCw,
  Zap,
  Clock,
  DollarSign,
  Save,
  Tag,
  Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminSettingsPage = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [lastExport, setLastExport] = useState(null);
  const [lastImport, setLastImport] = useState(null);
  const [lastFix, setLastFix] = useState(null);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [autoBackups, setAutoBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [cacheSettings, setCacheSettings] = useState({
    catalog_cache_enabled: true,
    catalog_cache_start_hour: 6,
    catalog_cache_interval: 6,
    catalog_cache_interval_unit: 'hours'
  });
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheSaving, setCacheSaving] = useState(false);
  const [cacheRefreshing, setCacheRefreshing] = useState(false);
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorLogsUnread, setErrorLogsUnread] = useState(0);
  const [errorLogsLoading, setErrorLogsLoading] = useState(false);
  const [costExplanations, setCostExplanations] = useState({
    exchange_title: '',
    exchange_items: '',
    return_title: '',
    return_items: '',
    return_note: '',
    bridal_warning: '',
    elite_warning: '',
    bridal_intro: '',
    bridal_whatsapp_text: ''
  });
  const [costExpSaving, setCostExpSaving] = useState(false);
  const [showExchangeCostExpl, setShowExchangeCostExpl] = useState(true);
  const [showReturnCostExpl, setShowReturnCostExpl] = useState(true);
  const [shippingLabels, setShippingLabels] = useState({
    shopify_shipping_label: 'Spedizione autonoma del cliente',
    shopify_shipping_label_store: 'Consegna in negozio'
  });
  const [shippingLabelsSaving, setShippingLabelsSaving] = useState(false);

  useEffect(() => {
    // Fetch webhook status on mount
    const fetchWebhookStatus = async () => {      try {
        const response = await fetch(`${API_BASE}/webhooks/status`);
        if (response.ok) {
          const data = await response.json();
          // Override endpoint URLs with current origin to always show correct domain
          const currentOrigin = window.location.origin;
          if (data.webhooks) {
            data.webhooks = data.webhooks.map(wh => ({
              ...wh,
              endpoint: `${currentOrigin}/api/webhooks/${wh.event.replace('orders/', 'orders-')}`
            }));
          }
          if (data.webhook_endpoint) {
            data.webhook_endpoint = `${currentOrigin}/api/webhooks/orders-paid`;
          }
          if (data.setup_instructions) {
            const paidUrl = `${currentOrigin}/api/webhooks/orders-paid`;
            const fulfilledUrl = `${currentOrigin}/api/webhooks/orders-fulfilled`;
            data.setup_instructions = {
              ...data.setup_instructions,
              step_4: `URL Pagamento: ${paidUrl}`,
              step_5: `URL Evasione: ${fulfilledUrl}`,
            };
          }
          setWebhookStatus(data);
        }
      } catch (error) {
        console.error('Error fetching webhook status:', error);
      }
    };
    fetchWebhookStatus();
    
    // Fetch auto backups
    fetchAutoBackups();
    
    // Fetch cache settings
    fetchCacheSettings();
    
    // Fetch error logs
    fetchErrorLogs();
    
    // Fetch cost explanations
    fetchCostExplanations();
  }, []);

  const fetchAutoBackups = async () => {
    setLoadingBackups(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/backup/auto-backups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAutoBackups(data.backups || []);
      }
    } catch (error) {
      console.error('Error fetching auto backups:', error);
    }
    setLoadingBackups(false);
  };

  const fetchCacheSettings = async () => {
    setCacheLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCacheSettings({
          catalog_cache_enabled: data.catalog_cache_enabled !== false,
          catalog_cache_start_hour: data.catalog_cache_start_hour ?? 6,
          catalog_cache_interval: data.catalog_cache_interval ?? 6,
          catalog_cache_interval_unit: data.catalog_cache_interval_unit || 'hours'
        });
        // Also load shipping labels from same settings
        setShippingLabels({
          shopify_shipping_label: data.shopify_shipping_label || 'Spedizione autonoma del cliente',
          shopify_shipping_label_store: data.shopify_shipping_label_store || 'Consegna in negozio'
        });
      }
    } catch (error) {
      console.error('Error fetching cache settings:', error);
    }
    setCacheLoading(false);
  };

  const fetchErrorLogs = async () => {
    setErrorLogsLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/error-logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setErrorLogs(data.logs || []);
        setErrorLogsUnread(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching error logs:', error);
    }
    setErrorLogsLoading(false);
  };

  const markErrorLogsRead = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_BASE}/admin/error-logs/mark-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setErrorLogsUnread(0);
      setErrorLogs(prev => prev.map(l => ({ ...l, read: true })));
    } catch (e) { console.error('Mark logs read failed:', e); }
  };

  const clearErrorLogs = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_BASE}/admin/error-logs`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setErrorLogs([]);
      setErrorLogsUnread(0);
      toast.success('Log errori cancellati');
    } catch (e) { console.error('Clear logs failed:', e); }
  };

  const fetchCostExplanations = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const ce = data.cost_explanations || {};
        setCostExplanations({
          exchange_title: ce.exchange_title || '',
          exchange_items: ce.exchange_items || '',
          return_title: ce.return_title || '',
          return_items: ce.return_items || '',
          return_note: ce.return_note || '',
          bridal_warning: ce.bridal_warning || '',
          elite_warning: ce.elite_warning || '',
          bridal_intro: ce.bridal_intro || '',
          bridal_whatsapp_text: ce.bridal_whatsapp_text || ''
        });
        setShowExchangeCostExpl(data.show_exchange_cost_explanation !== false);
        setShowReturnCostExpl(data.show_return_cost_explanation !== false);
      }
    } catch (error) {
      console.error('Error fetching cost explanations:', error);
    }
  };

  const saveCostExplanations = async () => {
    setCostExpSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cost_explanations: costExplanations,
          show_exchange_cost_explanation: showExchangeCostExpl,
          show_return_cost_explanation: showReturnCostExpl
        })
      });
      if (response.ok) {
        toast.success('Testi costi salvati con successo');
      }
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
    setCostExpSaving(false);
  };

  const handleSaveCacheSettings = async () => {
    setCacheSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          catalog_cache_enabled: cacheSettings.catalog_cache_enabled,
          catalog_cache_start_hour: parseInt(cacheSettings.catalog_cache_start_hour) || 6,
          catalog_cache_interval: parseInt(cacheSettings.catalog_cache_interval) || 6,
          catalog_cache_interval_unit: cacheSettings.catalog_cache_interval_unit
        })
      });
      if (response.ok) {
        toast.success('Impostazioni cache salvate');
      } else {
        throw new Error();
      }
    } catch (e) {
      toast.error('Errore nel salvataggio');
    }
    setCacheSaving(false);
  };

  const handleRefreshCache = async () => {
    setCacheRefreshing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/products/refresh-cache`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Cache aggiornata');
      } else {
        throw new Error();
      }
    } catch (e) {
      toast.error('Errore nell\'aggiornamento della cache');
    }
    setCacheRefreshing(false);
  };

  const downloadAutoBackup = async (filename) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/backup/auto-backups/${filename}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Backup scaricato');
      }
    } catch (error) {
      toast.error('Errore nel download del backup');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiato negli appunti');
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/backup/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Errore durante l\'export');
      
      const data = await response.json();
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `backup_deafea_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      setLastExport({
        date: new Date().toLocaleString('it-IT'),
        counts: data.counts
      });
      
      toast.success('Backup esportato con successo', {
        description: `${data.counts.return_requests} resi, ${data.counts.rma_requests} RMA`
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Errore durante l\'export');
    }
    setIsExporting(false);
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/backup/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) throw new Error('Errore durante l\'import');
      
      const result = await response.json();
      
      setLastImport({
        date: new Date().toLocaleString('it-IT'),
        merged: result.merged
      });
      
      toast.success(result.message);
      
      // Refresh auto backups list
      fetchAutoBackups();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Errore durante l\'import. Verifica che il file sia valido.');
    }
    setIsImporting(false);
    // Reset file input
    event.target.value = '';
  };

  const handleFixRMACosts = async () => {
    setIsFixing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/fix-rma-costs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Errore durante la correzione');
      
      const result = await response.json();
      
      setLastFix({
        date: new Date().toLocaleString('it-IT'),
        fixed: result.fixed
      });
      
      if (result.fixed.length > 0) {
        toast.success(result.message, {
          description: result.fixed.map(f => f.order_number).join(', ')
        });
      } else {
        toast.info('Nessun RMA da correggere');
      }
    } catch (error) {
      console.error('Fix error:', error);
      toast.error('Errore durante la correzione');
    }
    setIsFixing(false);
  };

  return (
    <div className="p-4 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-heading font-semibold text-gray-900 flex items-center gap-3">
          <Settings className="w-7 h-7 text-gray-400" />
          Impostazioni
        </h1>
        <p className="text-gray-500 text-sm mt-1">Gestisci backup e impostazioni del sistema</p>
      </div>

      {/* Backup Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Database className="w-5 h-5 text-gray-500" />
            Backup Database
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Esporta o importa i dati delle richieste per backup o sincronizzazione
          </p>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-6">
          {/* Export Section */}
          <div className="border border-gray-200 rounded-lg p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Download className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Esporta Backup</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Scarica un file JSON con tutti i dati delle richieste (resi, cambi, voucher, RMA)
                </p>
                
                <Button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="mt-4 bg-blue-600 hover:bg-blue-700"
                  data-testid="export-backup-btn"
                >
                  {isExporting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Esportazione...
                    </>
                  ) : (
                    <>
                      <FileJson className="w-4 h-4 mr-2" />
                      Esporta JSON
                    </>
                  )}
                </Button>

                {lastExport && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-blue-700">
                      <CheckCircle className="w-4 h-4" />
                      <span>Ultimo export: {lastExport.date}</span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      {lastExport.counts.return_requests} resi, {lastExport.counts.rma_requests} RMA
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Import Section */}
          <div className="border border-gray-200 rounded-lg p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Upload className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Importa Backup</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Carica un file JSON di backup per ripristinare i dati (non sovrascrive dati esistenti)
                </p>
                
                <label className="inline-block mt-4">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImport}
                    disabled={isImporting}
                    className="hidden"
                    data-testid="import-backup-input"
                  />
                  <span className={cn(
                    "inline-flex items-center px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors",
                    isImporting 
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-emerald-600 text-white hover:bg-emerald-700"
                  )}>
                    {isImporting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importazione...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Seleziona File JSON
                      </>
                    )}
                  </span>
                </label>

                {lastImport && (
                  <div className="mt-4 p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      <span>Ultimo import: {lastImport.date}</span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1">
                      {lastImport.merged?.return_requests_added || 0} resi aggiunti, {lastImport.merged?.rma_requests_added || 0} RMA aggiunti
                      {(lastImport.merged?.return_requests_skipped > 0 || lastImport.merged?.rma_requests_skipped > 0) && (
                        <> (già esistenti: {lastImport.merged?.return_requests_skipped || 0} resi, {lastImport.merged?.rma_requests_skipped || 0} RMA)</>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Auto Backups Section */}
        <div className="px-6 pb-4">
          <div className="border border-purple-200 bg-purple-50 rounded-lg p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Archive className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Backup Automatici</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Backup creati automaticamente prima di ogni import (protezione anti-perdita dati)
                </p>
                
                {loadingBackups ? (
                  <div className="mt-4 flex items-center gap-2 text-purple-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Caricamento...</span>
                  </div>
                ) : autoBackups.length > 0 ? (
                  <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
                    {autoBackups.map((backup, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg border border-purple-100">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{backup.filename}</p>
                          <p className="text-xs text-gray-500">
                            {backup.counts?.return_requests || 0} resi, {backup.counts?.rma_requests || 0} RMA • {backup.size_kb} KB
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadAutoBackup(backup.filename)}
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Scarica
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-purple-600">
                    Nessun backup automatico. Verranno creati automaticamente al prossimo import.
                  </p>
                )}
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchAutoBackups}
                  className="mt-3 text-purple-600 border-purple-200 hover:bg-purple-50"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Aggiorna Lista
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Warning */}
        <div className="px-6 pb-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-emerald-800">Import sicuro (Merge)</p>
              <p className="text-sm text-emerald-700 mt-1">
                L'import aggiunge solo i record mancanti, <strong>senza cancellare</strong> quelli esistenti. I tuoi dati sono al sicuro.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Catalog Cache Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200 bg-teal-50">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-teal-600" />
            Catalogo Shopify
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Gestisci la cache dei prodotti per migliorare le prestazioni del catalogo
          </p>
        </div>

        <div className="p-6 space-y-6">
          {cacheLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
              <span className="ml-2 text-sm text-gray-500">Caricamento impostazioni...</span>
            </div>
          ) : (
            <>
              {/* Mode Toggle */}
              <div className="border border-gray-200 rounded-lg p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      "w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0",
                      cacheSettings.catalog_cache_enabled ? "bg-teal-100" : "bg-gray-100"
                    )}>
                      {cacheSettings.catalog_cache_enabled ? (
                        <Zap className="w-6 h-6 text-teal-600" />
                      ) : (
                        <RefreshCw className="w-6 h-6 text-gray-500" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Modalità: {cacheSettings.catalog_cache_enabled ? 'CACHE' : 'LIVE'}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {cacheSettings.catalog_cache_enabled
                          ? 'I prodotti vengono caricati dalla cache interna per prestazioni ottimali.'
                          : 'I prodotti vengono caricati direttamente da Shopify ad ogni richiesta (più lento).'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setCacheSettings(prev => ({ ...prev, catalog_cache_enabled: !prev.catalog_cache_enabled }))}
                    className={cn(
                      "relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0",
                      cacheSettings.catalog_cache_enabled ? "bg-teal-600" : "bg-gray-300"
                    )}
                    data-testid="cache-toggle"
                  >
                    <span className={cn(
                      "inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm",
                      cacheSettings.catalog_cache_enabled ? "translate-x-6" : "translate-x-1"
                    )} />
                  </button>
                </div>
              </div>

              {/* Cache Interval Settings — only shown when cache is enabled */}
              {cacheSettings.catalog_cache_enabled && (
                <div className="border border-gray-200 rounded-lg p-5">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Durata Cache</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Configura per quanto tempo i prodotti restano in cache prima di essere ricaricati da Shopify.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ml-16">
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1.5">
                        Intervallo
                      </label>
                      <Input
                        type="number"
                        min="1"
                        max="72"
                        value={cacheSettings.catalog_cache_interval}
                        onChange={(e) => setCacheSettings(prev => ({ ...prev, catalog_cache_interval: e.target.value }))}
                        className="rounded-md"
                        data-testid="cache-interval-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1.5">
                        Unità
                      </label>
                      <select
                        value={cacheSettings.catalog_cache_interval_unit}
                        onChange={(e) => setCacheSettings(prev => ({ ...prev, catalog_cache_interval_unit: e.target.value }))}
                        className="w-full h-10 rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        data-testid="cache-interval-unit"
                      >
                        <option value="hours">Ore</option>
                        <option value="minutes">Minuti</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleSaveCacheSettings}
                  disabled={cacheSaving}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="save-cache-settings-btn"
                >
                  {cacheSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Salva Impostazioni
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleRefreshCache}
                  disabled={cacheRefreshing}
                  variant="outline"
                  className="border-teal-300 text-teal-700 hover:bg-teal-50"
                  data-testid="refresh-cache-btn"
                >
                  {cacheRefreshing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Aggiornamento cache...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Aggiorna Cache Adesso
                    </>
                  )}
                </Button>
              </div>

              {/* Info box */}
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-start gap-3">
                <Zap className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-teal-800">Come funziona la cache</p>
                  <p className="text-sm text-teal-700 mt-1">
                    In modalità <strong>CACHE</strong>, i prodotti vengono scaricati da Shopify una sola volta e memorizzati internamente.
                    Le richieste successive usano i dati in cache, riducendo drasticamente i tempi di caricamento per i clienti.
                    Usa "Aggiorna Cache Adesso" per forzare un aggiornamento immediato.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* Fix RMA Costs Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200 bg-amber-50">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-amber-600" />
            Correzione Dati
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Correggi errori nei dati esistenti
          </p>
        </div>

        <div className="p-6">
          <div className="border border-gray-200 rounded-lg p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Wrench className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Fix Costi RMA</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Corregge gli RMA che hanno costo di gestione €0 (errore). Imposta il costo corretto a €35 e ricalcola il rimborso.
                </p>
                
                <Button
                  onClick={handleFixRMACosts}
                  disabled={isFixing}
                  className="mt-4 bg-amber-600 hover:bg-amber-700"
                  data-testid="fix-rma-costs-btn"
                >
                  {isFixing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Correzione in corso...
                    </>
                  ) : (
                    <>
                      <Wrench className="w-4 h-4 mr-2" />
                      Correggi Costi RMA
                    </>
                  )}
                </Button>

                {lastFix && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-amber-700">
                      <CheckCircle className="w-4 h-4" />
                      <span>Ultima correzione: {lastFix.date}</span>
                    </div>
                    {lastFix.fixed.length > 0 ? (
                      <div className="mt-2 text-xs text-amber-600">
                        <p className="font-medium mb-1">RMA corretti:</p>
                        {lastFix.fixed.map((f, i) => (
                          <p key={f.order_number || `fix-${i}`}>
                            {f.order_number} ({f.customer_name}): €{f.old_refund} → €{f.new_refund}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-amber-600 mt-1">Nessun RMA da correggere</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Shopify Shipping Labels Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12 }}
        className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200 bg-indigo-50">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Tag className="w-5 h-5 text-indigo-600" />
            Etichette Spedizione Shopify
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Testo che appare come metodo di spedizione negli ordini di cambio su Shopify
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Spedizione standard (ritiro a domicilio / spedizione autonoma)
            </label>
            <Input
              value={shippingLabels.shopify_shipping_label}
              onChange={(e) => setShippingLabels(prev => ({ ...prev, shopify_shipping_label: e.target.value }))}
              placeholder="Spedizione autonoma del cliente"
              data-testid="shopify-shipping-label-input"
            />
            <p className="text-xs text-gray-400 mt-1">Appare negli ordini di cambio con spedizione a domicilio</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Consegna in negozio
            </label>
            <Input
              value={shippingLabels.shopify_shipping_label_store}
              onChange={(e) => setShippingLabels(prev => ({ ...prev, shopify_shipping_label_store: e.target.value }))}
              placeholder="Consegna in negozio"
              data-testid="shopify-shipping-label-store-input"
            />
            <p className="text-xs text-gray-400 mt-1">Appare negli ordini di cambio con consegna in negozio</p>
          </div>
          <Button
            onClick={async () => {
              setShippingLabelsSaving(true);
              try {
                const token = localStorage.getItem('admin_token');
                const response = await fetch(`${API_BASE}/admin/settings`, {
                  method: 'PUT',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify(shippingLabels)
                });
                if (response.ok) toast.success('Etichette spedizione salvate');
                else throw new Error();
              } catch { toast.error('Errore nel salvataggio'); }
              setShippingLabelsSaving(false);
            }}
            disabled={shippingLabelsSaving}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-shipping-labels-btn"
          >
            {shippingLabelsSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Salva Etichette
          </Button>
        </div>
      </motion.div>

      {/* Webhook Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200 bg-purple-50">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Webhook className="w-5 h-5 text-purple-600" />
            Webhook Shopify
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Automazione pagamento ed evasione ordini di cambio
          </p>
        </div>

        <div className="p-6">
          {webhookStatus ? (
            <div className="space-y-4">
              {/* Webhook URLs — show each webhook */}
              {webhookStatus.webhooks && webhookStatus.webhooks.map((wh, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold">
                      {idx + 1}
                    </span>
                    <p className="text-sm font-semibold text-gray-900">{wh.name}</p>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-mono">{wh.event}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{wh.description}</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-xs font-mono text-gray-800 overflow-x-auto">
                      {wh.endpoint}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(wh.endpoint)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}

              {/* Fallback: single webhook_endpoint for old format */}
              {!webhookStatus.webhooks && webhookStatus.webhook_endpoint && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">URL Webhook</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono text-gray-800 overflow-x-auto">
                      {webhookStatus.webhook_endpoint}
                    </code>
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookStatus.webhook_endpoint)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500">
                  Verifica HMAC: <span className={webhookStatus.hmac_verification === 'enabled' ? 'text-green-600 font-medium' : 'text-amber-600'}>
                    {webhookStatus.hmac_verification}
                  </span>
                  {' '} · Versione API: <span className="font-medium text-gray-700">{webhookStatus.api_version}</span>
                </p>
              </div>

              {/* Setup Instructions */}
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Istruzioni di Configurazione</p>
                <ol className="space-y-2 text-sm text-gray-600">
                  {Object.entries(webhookStatus.setup_instructions).map(([key, value]) => (
                    <li key={key} className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        {key.replace('step_', '')}
                      </span>
                      <span>{value}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Production Note */}
              {webhookStatus.note && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <ExternalLink className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800">{webhookStatus.note}</p>
                  </div>
                </div>
              )}

              {/* How it works */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Come funziona</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Quando un cliente richiede un <strong>Cambio</strong> con pagamento, viene creato un Draft Order su Shopify</li>
                  <li>• Il cliente riceve un link per pagare</li>
                  <li>• Quando paga, Shopify invia la notifica <strong>orders/paid</strong> → stato aggiornato a "Pagato"</li>
                  <li>• L'admin lavora il cambio e evade l'ordine su Shopify</li>
                  <li>• Shopify invia la notifica <strong>orders/fulfilled</strong> → stato aggiornato a "Completato" automaticamente</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Caricamento configurazione webhook...
            </div>
          )}
        </div>
      </motion.div>

      {/* Info Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-6 bg-white rounded-xl border border-gray-200 p-6"
      >
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-500" />
          Come usare il backup
        </h3>
        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
            <p><strong>Backup periodico:</strong> Esporta regolarmente i dati dalla produzione per avere un backup di sicurezza.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
            <p><strong>Sincronizza ambienti:</strong> Esporta dalla produzione, poi importa in preview per testare con dati reali.</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
            <p><strong>Ripristino:</strong> In caso di problemi, puoi importare un backup precedente per recuperare i dati.</p>
          </div>
        </div>
      </motion.div>

      {/* Cost Explanations Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12 }}
        className="mt-6 bg-white rounded-xl border border-gray-200 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-amber-500" />
            Testi Spiegazione Costi
          </h3>
          <Button size="sm" onClick={saveCostExplanations} disabled={costExpSaving}>
            <Save className={cn("w-4 h-4 mr-1", costExpSaving && "animate-spin")} />
            {costExpSaving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </div>

        <p className="text-xs text-gray-500 mb-6">
          Questi testi vengono mostrati al cliente durante la scelta, nel riepilogo e nelle email.
          Per le voci lista, separa ogni voce con il carattere <strong>|</strong> (pipe).
          Usa <strong>{'{bridal_cost}'}</strong> come segnaposto per il costo sposa configurato.
        </p>

        {/* Toggle visibilità */}
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Visibilità al cliente</h4>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Mostra "Perché il cambio ha questo costo?"</p>
              <p className="text-xs text-gray-500">Visibile nel riepilogo cambio e nell'email al cliente</p>
            </div>
            <Switch checked={showExchangeCostExpl} onCheckedChange={setShowExchangeCostExpl} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Mostra "Perché il reso ha questo costo?"</p>
              <p className="text-xs text-gray-500">Visibile nel flusso reso e nell'email al cliente</p>
            </div>
            <Switch checked={showReturnCostExpl} onCheckedChange={setShowReturnCostExpl} />
          </div>
        </div>

        {/* Exchange */}
        {showExchangeCostExpl && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-800 mb-3">Cambio</h4>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600">Titolo</label>
              <Input
                value={costExplanations.exchange_title}
                onChange={e => setCostExplanations(p => ({ ...p, exchange_title: e.target.value }))}
                placeholder="Perché il cambio ha questo costo?"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Voci spesa (separate da |)</label>
              <Textarea
                value={costExplanations.exchange_items}
                onChange={e => setCostExplanations(p => ({ ...p, exchange_items: e.target.value }))}
                placeholder="Spedizione di andata - circa 13€|Spedizione di ritiro - circa 13€"
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
        </div>
        )}

        {/* Return/RMA */}
        {showReturnCostExpl && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h4 className="text-sm font-semibold text-amber-800 mb-3">Reso</h4>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600">Titolo</label>
              <Input
                value={costExplanations.return_title}
                onChange={e => setCostExplanations(p => ({ ...p, return_title: e.target.value }))}
                placeholder="Perché il reso ha questo costo?"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Voci spesa (separate da |)</label>
              <Textarea
                value={costExplanations.return_items}
                onChange={e => setCostExplanations(p => ({ ...p, return_items: e.target.value }))}
                placeholder="Spedizione di andata - circa 13€|Ritiro a domicilio - circa 13€|..."
                rows={3}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Nota aggiuntiva</label>
              <Textarea
                value={costExplanations.return_note}
                onChange={e => setCostExplanations(p => ({ ...p, return_note: e.target.value }))}
                placeholder="Il costo del reso riflette un servizio completo..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
        </div>
        )}

        {/* Bridal / Elite */}
        <div className="p-4 bg-pink-50 border border-pink-200 rounded-lg">
          <h4 className="text-sm font-semibold text-pink-800 mb-3">Collezione Sposa & Elite</h4>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600">Avviso sposa (nel riepilogo)</label>
              <Textarea
                value={costExplanations.bridal_warning}
                onChange={e => setCostExplanations(p => ({ ...p, bridal_warning: e.target.value }))}
                placeholder="Gli abiti della collezione sposa hanno un costo fisso di €{bridal_cost}..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Introduzione sposa (pagina scelta tipo)</label>
              <Textarea
                value={costExplanations.bridal_intro}
                onChange={e => setCostExplanations(p => ({ ...p, bridal_intro: e.target.value }))}
                placeholder="Il tuo ordine contiene un abito della collezione sposa..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Testo WhatsApp sposa</label>
              <Textarea
                value={costExplanations.bridal_whatsapp_text}
                onChange={e => setCostExplanations(p => ({ ...p, bridal_whatsapp_text: e.target.value }))}
                placeholder="Per procedere con la tua richiesta, ti chiediamo di contattarci..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Avviso Elite Collection</label>
              <Textarea
                value={costExplanations.elite_warning}
                onChange={e => setCostExplanations(p => ({ ...p, elite_warning: e.target.value }))}
                placeholder="I capi della Elite Collection hanno un costo di gestione di €45..."
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Email Logs Export */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12 }}
        className="mt-6 bg-white rounded-xl border border-gray-200 p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-500" />
              Log Email Backend
            </h3>
            <p className="text-xs text-gray-500 mt-1">Esporta il registro completo di tutte le email inviate, bloccate o fallite per ogni richiesta.</p>
          </div>
          <Button size="sm" variant="outline" onClick={async () => {
            try {
              const token = localStorage.getItem('admin_token');
              const res = await fetch(`${API_BASE}/admin/email-logs/export`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const data = await res.json();
              const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `email_logs_${new Date().toISOString().slice(0,10)}.json`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success(`${data.length} log email scaricati`);
            } catch (e) { toast.error('Errore nel download'); }
          }} data-testid="download-email-logs-btn">
            <Download className="w-4 h-4 mr-1" />
            Scarica Log Email
          </Button>
        </div>
      </motion.div>

      {/* Error Logs Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="mt-6 bg-white rounded-xl border border-gray-200 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Log Errori Frontend
            {errorLogsUnread > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{errorLogsUnread}</span>
            )}
          </h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={fetchErrorLogs} disabled={errorLogsLoading}>
              <RefreshCw className={cn("w-4 h-4 mr-1", errorLogsLoading && "animate-spin")} />
              Aggiorna
            </Button>
            {errorLogsUnread > 0 && (
              <Button size="sm" variant="outline" onClick={markErrorLogsRead}>
                Segna come letti
              </Button>
            )}
            {errorLogs.length > 0 && (
              <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={clearErrorLogs}>
                Cancella tutti
              </Button>
            )}
            {errorLogs.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => {
                const data = JSON.stringify(errorLogs, null, 2);
                const blob = new Blob([data], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `error_logs_${new Date().toISOString().slice(0,10)}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }} data-testid="download-error-logs-btn">
                <Download className="w-4 h-4 mr-1" />
                Scarica
              </Button>
            )}
          </div>
        </div>

        {errorLogsLoading ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento...
          </div>
        ) : errorLogs.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
            <p className="text-sm">Nessun errore registrato</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {errorLogs.map((log, i) => (
              <div key={log.timestamp || log.id || `log-${i}`} className={cn(
                "border rounded-lg p-4 text-sm",
                log.read ? "border-gray-200 bg-white" : "border-red-200 bg-red-50"
              )}>
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {!log.read && <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />}
                      <span className="font-medium text-gray-900">{log.customer_email || 'Email non disponibile'}</span>
                      {log.customer_name && <span className="text-gray-500">({log.customer_name})</span>}
                      {log.order_number && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{log.order_number}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {log.page_url && <span className="mr-3">{log.page_url}</span>}
                      {log.created_at && new Date(log.created_at.endsWith?.('Z') ? log.created_at : log.created_at + 'Z').toLocaleString('it-IT', { timeZone: 'Europe/Rome' })}
                    </p>
                  </div>
                </div>
                <div className="bg-gray-900 text-red-300 rounded p-3 font-mono text-xs overflow-x-auto">
                  <p className="text-white font-semibold mb-1">{log.error_message}</p>
                  {log.component_stack && (
                    <pre className="text-gray-400 whitespace-pre-wrap mt-1 max-h-32 overflow-y-auto">{log.component_stack}</pre>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminSettingsPage;
